package features.booking

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.foundation.BorderStroke
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch

data class WaypointData(val lat: Double, val lng: Double, val nombre: String, val tipo: String = "")
// tipo: "subida" (verde, salida) | "bajada" (rojo, destino) | "" (intermedio)

@Composable
fun EditorRutasMapa(
    ciudadesDisponibles: List<String> = emptyList(),
    paradasInfoDisponibles: List<ParadaInfo> = emptyList(),
    puntosSubidaInfoDisponibles: List<ParadaInfo> = emptyList()
) {
    val scope = rememberCoroutineScope()
    val smartvanDark = SmartVanColors.AzulOscuro
    val smartvanCyan = SmartVanColors.CyanAccent

    var rutas by remember { mutableStateOf<List<String>>(emptyList()) }
    var rutaSeleccionada by remember { mutableStateOf<String?>(null) }
    var waypoints by remember { mutableStateOf<List<WaypointData>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var mensaje by remember { mutableStateOf<String?>(null) }
    var mostrarDialogoNuevaRuta by remember { mutableStateOf(false) }

    // Para el diálogo de nueva ruta con dropdowns
    var origenSeleccionado by remember { mutableStateOf("") }
    var destinoSeleccionado by remember { mutableStateOf("") }
    var expandedOrigen by remember { mutableStateOf(false) }
    var expandedDestino by remember { mutableStateOf(false) }
    var creandoRuta by remember { mutableStateOf(false) }

    // Para el diálogo de eliminar ruta
    var rutaAEliminar by remember { mutableStateOf<String?>(null) }
    var eliminandoRuta by remember { mutableStateOf(false) }

    // Campos para nuevo waypoint manual
    var nuevoNombre by remember { mutableStateOf("") }
    var nuevoLat by remember { mutableStateOf("") }
    var nuevoLng by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        try {
            val db = network.FirebaseClient.database
            val snap = db.reference("catalogos/rutas").valueEvents.first()
            if (snap.exists) {
                rutas = snap.children.mapNotNull { it.key }
            }
        } catch (e: Exception) {
        }
        isLoading = false
    }

    // Cargar waypoints cuando se selecciona una ruta
    LaunchedEffect(rutaSeleccionada) {
        val ruta = rutaSeleccionada ?: return@LaunchedEffect
        try {
            val db = network.FirebaseClient.database
            val snap = db.reference("catalogos/rutas/$ruta/waypoints").valueEvents.first()
            if (snap.exists) {
                val lista = mutableListOf<WaypointData>()
                snap.children.forEach { child ->
                    try {
                        val lat = try { child.child("lat").value<Double>() } catch (e: Exception) { 0.0 }
                        val lng = try { child.child("lng").value<Double>() } catch (e: Exception) { 0.0 }
                        val nombre = try { child.child("nombre").value<String>() } catch (e: Exception) { "" }
                        val tipo = try { child.child("tipo").value<String>() } catch (e: Exception) { "" }
                        lista.add(WaypointData(lat, lng, nombre, tipo))
                    } catch (e: Exception) {}
                }
                waypoints = lista
            } else {
                waypoints = emptyList()
            }
        } catch (e: Exception) {
            waypoints = emptyList()
        }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(SmartVanColors.FondoClaro)
            .verticalScroll(rememberScrollState())
            .padding(16.dp)
    ) {
        // Título
        Text(
            "Rutas en Mapa",
            style = MaterialTheme.typography.headlineSmall,
            fontWeight = FontWeight.Bold,
            color = smartvanDark
        )
        Text(
            "Define los puntos de la ruta que verán los pasajeros en el mapa de rastreo",
            color = Color.Gray,
            fontSize = 13.sp
        )

        Spacer(modifier = Modifier.height(16.dp))

        if (isLoading) {
            CircularProgressIndicator(color = smartvanCyan)
        } else {
            // Selector de ruta
            Text("Selecciona una ruta:", fontWeight = FontWeight.Bold, color = smartvanDark)
            Spacer(modifier = Modifier.height(8.dp))
            FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                rutas.forEach { ruta ->
                    FilterChip(
                        selected = rutaSeleccionada == ruta,
                        onClick = { rutaSeleccionada = ruta },
                        label = { Text(ruta, fontSize = 13.sp) },
                        trailingIcon = {
                            if (rutaSeleccionada == ruta) {
                                Icon(
                                    Icons.Filled.Delete,
                                    contentDescription = "Eliminar ruta",
                                    tint = Color.Red.copy(alpha = 0.7f),
                                    modifier = Modifier.size(16.dp).clickable { rutaAEliminar = ruta }
                                )
                            }
                        },
                        colors = FilterChipDefaults.filterChipColors(
                            selectedContainerColor = smartvanCyan.copy(alpha = 0.2f)
                        )
                    )
                }
                // Botón para nueva ruta
                FilterChip(
                    selected = false,
                    onClick = { mostrarDialogoNuevaRuta = true },
                    label = { Text("+ Nueva", fontSize = 13.sp) },
                    leadingIcon = { Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(16.dp)) }
                )
            }

            if (rutaSeleccionada != null) {
                Spacer(modifier = Modifier.height(16.dp))

                // Lista de waypoints existentes
                Card(
                    colors = CardDefaults.cardColors(containerColor = Color.White),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        Text(
                            "Puntos de la ruta: ${rutaSeleccionada}",
                            fontWeight = FontWeight.Bold,
                            color = smartvanDark,
                            fontSize = 15.sp
                        )
                        Text(
                            "Subidas (origen) en verde, bajadas (destino) en rojo. Usa las flechas para ordenar la secuencia del recorrido.",
                            color = Color.Gray,
                            fontSize = 12.sp
                        )
                        Spacer(modifier = Modifier.height(12.dp))

                        if (waypoints.isEmpty()) {
                            Text("No hay waypoints definidos", color = Color.Gray, fontStyle = androidx.compose.ui.text.font.FontStyle.Italic)
                        } else {
                            waypoints.forEachIndexed { index, wp ->
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    // Color según tipo: subida=verde (salida), bajada=rojo, intermedio=naranja
                                    val iconColor = when (wp.tipo) {
                                        "subida" -> SmartVanColors.Verde
                                        "bajada" -> Color(0xFFEF5350)
                                        else -> SmartVanColors.Naranja
                                    }
                                    Box(
                                        modifier = Modifier.size(28.dp).background(iconColor, RoundedCornerShape(14.dp)),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        Text("${index + 1}", color = Color.White, fontWeight = FontWeight.Bold, fontSize = 12.sp)
                                    }
                                    Spacer(modifier = Modifier.width(10.dp))
                                    Column(modifier = Modifier.weight(1f)) {
                                        Row(verticalAlignment = Alignment.CenterVertically) {
                                            Text(wp.nombre.ifBlank { "Punto ${index + 1}" }, fontWeight = FontWeight.Medium, fontSize = 14.sp)
                                            Spacer(modifier = Modifier.width(6.dp))
                                            val etiquetaTipo = when (wp.tipo) {
                                                "subida" -> "Subida"
                                                "bajada" -> "Bajada"
                                                else -> ""
                                            }
                                            if (etiquetaTipo.isNotBlank()) {
                                                Box(
                                                    modifier = Modifier.background(iconColor.copy(alpha = 0.15f), RoundedCornerShape(6.dp)).padding(horizontal = 6.dp, vertical = 1.dp)
                                                ) { Text(etiquetaTipo, color = iconColor, fontSize = 10.sp, fontWeight = FontWeight.Bold) }
                                            }
                                        }
                                        Text("${wp.lat}, ${wp.lng}", color = Color.Gray, fontSize = 11.sp)
                                    }
                                    // Subir
                                    IconButton(
                                        onClick = {
                                            if (index > 0) waypoints = waypoints.toMutableList().also {
                                                val t = it[index]; it[index] = it[index - 1]; it[index - 1] = t
                                            }
                                        },
                                        enabled = index > 0,
                                        modifier = Modifier.size(32.dp)
                                    ) {
                                        Icon(Icons.Filled.KeyboardArrowUp, contentDescription = "Subir", tint = if (index > 0) smartvanDark else Color.LightGray, modifier = Modifier.size(20.dp))
                                    }
                                    // Bajar
                                    IconButton(
                                        onClick = {
                                            if (index < waypoints.lastIndex) waypoints = waypoints.toMutableList().also {
                                                val t = it[index]; it[index] = it[index + 1]; it[index + 1] = t
                                            }
                                        },
                                        enabled = index < waypoints.lastIndex,
                                        modifier = Modifier.size(32.dp)
                                    ) {
                                        Icon(Icons.Filled.KeyboardArrowDown, contentDescription = "Bajar", tint = if (index < waypoints.lastIndex) smartvanDark else Color.LightGray, modifier = Modifier.size(20.dp))
                                    }
                                    IconButton(onClick = {
                                        waypoints = waypoints.toMutableList().also { it.removeAt(index) }
                                    }, modifier = Modifier.size(32.dp)) {
                                        Icon(Icons.Filled.Delete, contentDescription = "Eliminar", tint = Color.Red, modifier = Modifier.size(20.dp))
                                    }
                                }
                                if (index < waypoints.lastIndex) {
                                    HorizontalDivider(modifier = Modifier.padding(start = 38.dp))
                                }
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                // Formulario para agregar waypoint
                Card(
                    colors = CardDefaults.cardColors(containerColor = Color.White),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Column(modifier = Modifier.padding(16.dp)) {
                        Text("Agregar punto", fontWeight = FontWeight.Bold, color = smartvanDark)
                        Text("Usa Google Maps para obtener las coordenadas: click derecho → 'Qué hay aquí'", color = Color.Gray, fontSize = 11.sp)
                        Spacer(modifier = Modifier.height(8.dp))

                        OutlinedTextField(
                            value = nuevoNombre,
                            onValueChange = { nuevoNombre = it },
                            label = { Text("Nombre del punto") },
                            placeholder = { Text("Ej: San Juan del Río") },
                            modifier = Modifier.fillMaxWidth(),
                            singleLine = true,
                            shape = RoundedCornerShape(10.dp)
                        )
                        Spacer(modifier = Modifier.height(8.dp))
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            OutlinedTextField(
                                value = nuevoLat,
                                onValueChange = { nuevoLat = it },
                                label = { Text("Latitud") },
                                placeholder = { Text("20.5888") },
                                modifier = Modifier.weight(1f),
                                singleLine = true,
                                shape = RoundedCornerShape(10.dp)
                            )
                            OutlinedTextField(
                                value = nuevoLng,
                                onValueChange = { nuevoLng = it },
                                label = { Text("Longitud") },
                                placeholder = { Text("-100.3899") },
                                modifier = Modifier.weight(1f),
                                singleLine = true,
                                shape = RoundedCornerShape(10.dp)
                            )
                        }
                        Spacer(modifier = Modifier.height(8.dp))
                        Button(
                            onClick = {
                                val lat = nuevoLat.toDoubleOrNull()
                                val lng = nuevoLng.toDoubleOrNull()
                                if (lat != null && lng != null && nuevoNombre.isNotBlank()) {
                                    waypoints = waypoints + WaypointData(lat, lng, nuevoNombre)
                                    nuevoNombre = ""
                                    nuevoLat = ""
                                    nuevoLng = ""
                                }
                            },
                            enabled = nuevoLat.toDoubleOrNull() != null && nuevoLng.toDoubleOrNull() != null && nuevoNombre.isNotBlank(),
                            colors = ButtonDefaults.buttonColors(containerColor = smartvanCyan),
                            shape = RoundedCornerShape(10.dp)
                        ) {
                            Icon(Icons.Filled.Add, contentDescription = null, modifier = Modifier.size(18.dp))
                            Spacer(modifier = Modifier.width(6.dp))
                            Text("Agregar Punto")
                        }
                    }
                }

                // Puntos por rol de ciudad: SUBIDAS del origen (verde) y BAJADAS del destino (rojo)
                val rutaParts = rutaSeleccionada?.split(" - ")?.map { it.trim() } ?: emptyList()
                val origenCiudad = rutaParts.getOrNull(0) ?: ""
                val destinoCiudad = rutaParts.getOrNull(1) ?: ""

                fun coincideCiudad(pi: ParadaInfo, ciudad: String): Boolean =
                    ciudad.isNotBlank() && pi.ciudad.isNotBlank() && pi.lat != 0.0 &&
                        (pi.ciudad.equals(ciudad, ignoreCase = true) ||
                            pi.ciudad.contains(ciudad, ignoreCase = true) ||
                            ciudad.contains(pi.ciudad, ignoreCase = true))

                val subidasDisponibles = puntosSubidaInfoDisponibles
                    .filter { coincideCiudad(it, origenCiudad) }
                    .filter { pi -> waypoints.none { it.nombre == pi.nombre } }
                val bajadasDisponibles = paradasInfoDisponibles
                    .filter { coincideCiudad(it, destinoCiudad) }
                    .filter { pi -> waypoints.none { it.nombre == pi.nombre } }

                // Card: SUBIDAS del origen (verde = salida)
                if (subidasDisponibles.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(12.dp))
                    PuntosSeleccionablesCard(
                        titulo = "Subidas en $origenCiudad (salida)",
                        subtitulo = "Puntos de salida del origen. Toca + para agregarlos al recorrido (verde).",
                        color = SmartVanColors.Verde,
                        puntos = subidasDisponibles,
                        onAgregar = { p -> waypoints = waypoints + WaypointData(p.lat, p.lng, p.nombre, "subida") },
                        onAgregarTodas = {
                            var acc = waypoints
                            subidasDisponibles.forEach { p -> acc = acc + WaypointData(p.lat, p.lng, p.nombre, "subida") }
                            waypoints = acc
                        }
                    )
                }

                // Card: BAJADAS del destino (rojo)
                if (bajadasDisponibles.isNotEmpty()) {
                    Spacer(modifier = Modifier.height(12.dp))
                    PuntosSeleccionablesCard(
                        titulo = "Bajadas en $destinoCiudad (destino)",
                        subtitulo = "Puntos de bajada del destino. Ordénalos con las flechas según la logística del recorrido (rojo).",
                        color = Color(0xFFEF5350),
                        puntos = bajadasDisponibles,
                        onAgregar = { p -> waypoints = waypoints + WaypointData(p.lat, p.lng, p.nombre, "bajada") },
                        onAgregarTodas = {
                            var acc = waypoints
                            bajadasDisponibles.forEach { p -> acc = acc + WaypointData(p.lat, p.lng, p.nombre, "bajada") }
                            waypoints = acc
                        }
                    )
                }

                Spacer(modifier = Modifier.height(12.dp))

                // Botón: generar waypoints automáticamente desde los puntos de subida (origen)
                // y bajada (destino) configurados para las ciudades de la ruta. Así la ruta la
                // marcan los PUNTOS REALES (Zakia, Zibata...) y no solo las ciudades.
                OutlinedButton(
                    onClick = {
                        val ruta = rutaSeleccionada ?: return@OutlinedButton
                        // La ruta viene como "Ciudad Origen - Ciudad Destino".
                        val partes = ruta.split(" - ", " – ", "-").map { it.trim() }.filter { it.isNotBlank() }
                        val ciudadOrigen = partes.firstOrNull() ?: ""
                        val ciudadDestino = partes.lastOrNull() ?: ""
                        // Puntos de subida del ORIGEN (marcadores verdes de salida).
                        val subida = puntosSubidaInfoDisponibles
                            .filter { it.ciudad.contains(ciudadOrigen, true) && it.lat != 0.0 && it.lng != 0.0 }
                            .map { WaypointData(it.lat, it.lng, it.nombre, "subida") }
                        // Puntos de bajada del DESTINO (marcadores rojos de llegada).
                        val bajada = paradasInfoDisponibles
                            .filter { it.ciudad.contains(ciudadDestino, true) && it.lat != 0.0 && it.lng != 0.0 }
                            .map { WaypointData(it.lat, it.lng, it.nombre, "bajada") }
                        val generados = subida + bajada
                        if (generados.size < 2) {
                            mensaje = "Necesitas al menos 1 punto de subida en $ciudadOrigen y 1 de bajada en $ciudadDestino con coordenadas."
                        } else {
                            waypoints = generados
                            mensaje = "Ruta generada: ${subida.size} punto(s) de subida + ${bajada.size} de bajada. Revisa y guarda."
                        }
                    },
                    modifier = Modifier.fillMaxWidth().height(46.dp),
                    shape = RoundedCornerShape(12.dp),
                    border = BorderStroke(1.dp, smartvanCyan)
                ) {
                    Icon(Icons.Filled.AutoFixHigh, contentDescription = null, tint = smartvanCyan, modifier = Modifier.size(18.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Generar desde puntos de subida/bajada", color = smartvanCyan, fontWeight = FontWeight.Bold, fontSize = 13.sp)
                }

                Spacer(modifier = Modifier.height(8.dp))

                // Botón guardar
                Button(
                    onClick = {
                        scope.launch {
                            try {
                                val db = network.FirebaseClient.database
                                // Diagnóstico: verificar que auth actual tenga rol admin+ antes de escribir.
                                val auth = network.FirebaseClient.auth
                                val uid = auth.currentUser?.uid
                                if (uid == null) {
                                    mensaje = "Error: no hay sesión activa. Vuelve a iniciar sesión."
                                    return@launch
                                }
                                val rolSnap = db.reference("usuarios/$uid/rol").valueEvents.first()
                                val rol = if (rolSnap.exists) try { rolSnap.value<String>() } catch (_: Exception) { "" } else ""
                                val rolesPermitidos = listOf("admin", "admin_general", "master", "desarrollador")
                                if (rol !in rolesPermitidos) {
                                    mensaje = "Error: tu rol actual ($rol) no puede editar rutas. Cierra sesión y vuelve a entrar."
                                    return@launch
                                }
                                val waypointsList = waypoints.map { mapOf("lat" to it.lat, "lng" to it.lng, "nombre" to it.nombre, "tipo" to it.tipo) }
                                db.reference("catalogos/rutas/${rutaSeleccionada}/waypoints").setValue(waypointsList)
                                mensaje = "Ruta guardada correctamente"
                            } catch (e: Exception) {
                                mensaje = "Error guardando: ${e.message}"
                            }
                        }
                    },
                    modifier = Modifier.fillMaxWidth().height(48.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = smartvanDark),
                    shape = RoundedCornerShape(12.dp),
                    enabled = waypoints.isNotEmpty()
                ) {
                    Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = Color.White)
                    Spacer(modifier = Modifier.width(8.dp))
                    Text("Guardar Ruta en Mapa", color = Color.White, fontWeight = FontWeight.Bold)
                }

                Spacer(modifier = Modifier.height(8.dp))

                // Botón Editar en Mapa (abre mapa interactivo con click para agregar puntos)
                var mapaAbierto by remember { mutableStateOf(false) }

                if (!mapaAbierto) {
                    OutlinedButton(
                        onClick = {
                            val ruta = rutaSeleccionada ?: return@OutlinedButton
                            val jsonArray = waypoints.joinToString(",", "[", "]") { wp ->
                                val nombreEscaped = wp.nombre.replace("\"", "\\\"")
                                """{"lat":${wp.lat},"lng":${wp.lng},"nombre":"$nombreEscaped","tipo":"${wp.tipo}"}"""
                            }
                            org.example.abigail.abrirVistaPreviaRuta(ruta, jsonArray)
                            mapaAbierto = true
                        },
                        modifier = Modifier.fillMaxWidth().height(48.dp),
                        shape = RoundedCornerShape(12.dp),
                        border = BorderStroke(2.dp, smartvanCyan)
                    ) {
                        Icon(Icons.Filled.Map, contentDescription = null, tint = smartvanCyan)
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            if (waypoints.isEmpty()) "Abrir Mapa para Trazar Ruta" else "Editar Ruta en Mapa",
                            color = smartvanDark, fontWeight = FontWeight.Bold
                        )
                    }
                } else {
                    // Botón para cerrar el mapa y recoger cambios
                    Button(
                        onClick = {
                            // Recoger waypoints actualizados del iframe
                            val actualizados = org.example.abigail.obtenerWaypointsActualizados()
                            if (actualizados != null && actualizados.isNotEmpty()) {
                                val tiposPrevios = waypoints.associate { it.nombre to it.tipo }
                                waypoints = actualizados.map { (lat, lng, nombre) ->
                                    WaypointData(lat, lng, nombre, tiposPrevios[nombre] ?: "")
                                }
                            }
                            org.example.abigail.cerrarMapaPreview()
                            mapaAbierto = false
                        },
                        modifier = Modifier.fillMaxWidth().height(48.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = SmartVanColors.Verde),
                        shape = RoundedCornerShape(12.dp)
                    ) {
                        Icon(Icons.Filled.CheckCircle, contentDescription = null, tint = Color.White)
                        Spacer(modifier = Modifier.width(8.dp))
                        Text("Cerrar Mapa y Aplicar Cambios", color = Color.White, fontWeight = FontWeight.Bold)
                    }
                }

                mensaje?.let {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(it, color = if (it.contains("Error")) Color.Red else SmartVanColors.Verde, fontWeight = FontWeight.Medium)
                }
            }
        }

        // Diálogo de confirmación para ELIMINAR ruta
        rutaAEliminar?.let { rutaElim ->
            AlertDialog(
                onDismissRequest = { if (!eliminandoRuta) rutaAEliminar = null },
                icon = { Icon(Icons.Filled.Delete, contentDescription = null, tint = Color.Red, modifier = Modifier.size(32.dp)) },
                title = { Text("Eliminar Ruta", fontWeight = FontWeight.Bold, color = Color.Red) },
                text = {
                    Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        Text(
                            "¿Estás seguro de eliminar la ruta \"$rutaElim\"?",
                            fontWeight = FontWeight.SemiBold,
                            color = smartvanDark
                        )
                        Text(
                            "Se eliminará el mapa y todos sus waypoints del catálogo. Los viajes ya creados con esta ruta NO se verán afectados.",
                            color = Color.Gray,
                            fontSize = 13.sp
                        )
                        if (eliminandoRuta) {
                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                CircularProgressIndicator(color = Color.Red, modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                                Text("Eliminando...", color = Color.Gray, fontSize = 12.sp)
                            }
                        }
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            eliminandoRuta = true
                            scope.launch {
                                try {
                                    val db = network.FirebaseClient.database
                                    db.reference("catalogos/rutas/$rutaElim").removeValue()
                                    rutas = rutas.filter { it != rutaElim }
                                    if (rutaSeleccionada == rutaElim) {
                                        rutaSeleccionada = null
                                        waypoints = emptyList()
                                    }
                                    mensaje = "Ruta '$rutaElim' eliminada correctamente"
                                } catch (e: Exception) {
                                    mensaje = "Error al eliminar: ${e.message}"
                                }
                                eliminandoRuta = false
                                rutaAEliminar = null
                            }
                        },
                        enabled = !eliminandoRuta,
                        colors = ButtonDefaults.buttonColors(containerColor = Color.Red)
                    ) {
                        Text("Sí, Eliminar", fontWeight = FontWeight.Bold, color = Color.White)
                    }
                },
                dismissButton = {
                    if (!eliminandoRuta) {
                        TextButton(onClick = { rutaAEliminar = null }) {
                            Text("Cancelar")
                        }
                    }
                }
            )
        }

        // Diálogo para crear nueva ruta con dropdowns de ciudades
        if (mostrarDialogoNuevaRuta) {
            val nombreRutaGenerado = if (origenSeleccionado.isNotBlank() && destinoSeleccionado.isNotBlank())
                "$origenSeleccionado - $destinoSeleccionado" else ""
            val nombreRutaInversa = if (origenSeleccionado.isNotBlank() && destinoSeleccionado.isNotBlank())
                "$destinoSeleccionado - $origenSeleccionado" else ""
            val rutaYaExiste = rutas.contains(nombreRutaGenerado)
            val rutaInversaExiste = rutas.contains(nombreRutaInversa)
            val puedeCrear = origenSeleccionado.isNotBlank() && destinoSeleccionado.isNotBlank()
                    && origenSeleccionado != destinoSeleccionado && !rutaYaExiste && !creandoRuta

            AlertDialog(
                onDismissRequest = {
                    if (!creandoRuta) {
                        mostrarDialogoNuevaRuta = false
                        origenSeleccionado = ""; destinoSeleccionado = ""
                    }
                },
                icon = { Icon(Icons.Filled.Add, contentDescription = null, tint = smartvanCyan, modifier = Modifier.size(32.dp)) },
                title = { Text("Nueva Ruta", fontWeight = FontWeight.Bold, color = smartvanDark) },
                text = {
                    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        Text("Selecciona origen y destino. Se generará una pre-ruta automática por carretera.", color = Color.Gray, fontSize = 13.sp)

                        // Dropdown Origen
                        Text("Origen:", fontWeight = FontWeight.SemiBold, color = smartvanDark, fontSize = 13.sp)
                        Box {
                            OutlinedTextField(
                                value = origenSeleccionado,
                                onValueChange = {},
                                readOnly = true,
                                label = { Text("Ciudad de salida") },
                                trailingIcon = {
                                    IconButton(onClick = { expandedOrigen = !expandedOrigen }) {
                                        Icon(Icons.Filled.ArrowDropDown, contentDescription = null)
                                    }
                                },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                shape = RoundedCornerShape(10.dp),
                                colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = smartvanCyan, cursorColor = smartvanCyan)
                            )
                            DropdownMenu(expanded = expandedOrigen, onDismissRequest = { expandedOrigen = false }) {
                                ciudadesDisponibles.forEach { ciudad ->
                                    DropdownMenuItem(
                                        text = { Text(ciudad) },
                                        onClick = { origenSeleccionado = ciudad; expandedOrigen = false }
                                    )
                                }
                            }
                        }

                        // Dropdown Destino
                        Text("Destino:", fontWeight = FontWeight.SemiBold, color = smartvanDark, fontSize = 13.sp)
                        Box {
                            OutlinedTextField(
                                value = destinoSeleccionado,
                                onValueChange = {},
                                readOnly = true,
                                label = { Text("Ciudad de llegada") },
                                trailingIcon = {
                                    IconButton(onClick = { expandedDestino = !expandedDestino }) {
                                        Icon(Icons.Filled.ArrowDropDown, contentDescription = null)
                                    }
                                },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                shape = RoundedCornerShape(10.dp),
                                colors = OutlinedTextFieldDefaults.colors(focusedBorderColor = smartvanCyan, cursorColor = smartvanCyan)
                            )
                            DropdownMenu(expanded = expandedDestino, onDismissRequest = { expandedDestino = false }) {
                                ciudadesDisponibles.filter { it != origenSeleccionado }.forEach { ciudad ->
                                    DropdownMenuItem(
                                        text = { Text(ciudad) },
                                        onClick = { destinoSeleccionado = ciudad; expandedDestino = false }
                                    )
                                }
                            }
                        }

                        // Preview del nombre de ruta
                        if (nombreRutaGenerado.isNotBlank()) {
                            Card(
                                colors = CardDefaults.cardColors(
                                    containerColor = if (rutaYaExiste) SmartVanColors.FondoRojo else Color(0xFFE3F2FD)
                                ),
                                shape = RoundedCornerShape(8.dp)
                            ) {
                                Row(modifier = Modifier.padding(12.dp), verticalAlignment = Alignment.CenterVertically) {
                                    Icon(
                                        if (rutaYaExiste) Icons.Filled.Warning else Icons.Filled.Map,
                                        contentDescription = null,
                                        tint = if (rutaYaExiste) Color.Red else smartvanCyan,
                                        modifier = Modifier.size(18.dp)
                                    )
                                    Spacer(modifier = Modifier.width(8.dp))
                                    Column {
                                        Text(
                                            nombreRutaGenerado,
                                            fontWeight = FontWeight.Bold,
                                            color = if (rutaYaExiste) Color.Red else smartvanDark,
                                            fontSize = 14.sp
                                        )
                                        if (rutaYaExiste) {
                                            Text("Esta ruta ya existe", color = Color.Red, fontSize = 11.sp)
                                        } else if (origenSeleccionado != destinoSeleccionado) {
                                            Text("Se creará con pre-ruta automática", color = Color.Gray, fontSize = 11.sp)
                                        }
                                    }
                                }
                            }
                            // Aviso informativo si existe la ruta inversa (pero SÍ se puede crear)
                            if (rutaInversaExiste && !rutaYaExiste) {
                                Card(
                                    colors = CardDefaults.cardColors(containerColor = SmartVanColors.FondoAmarillo),
                                    shape = RoundedCornerShape(8.dp)
                                ) {
                                    Row(modifier = Modifier.padding(10.dp), verticalAlignment = Alignment.CenterVertically) {
                                        Icon(Icons.Filled.Info, contentDescription = null, tint = Color(0xFFF57F17), modifier = Modifier.size(16.dp))
                                        Spacer(modifier = Modifier.width(8.dp))
                                        Text("Ya existe la ruta inversa ($nombreRutaInversa). Se creará un mapa independiente para esta dirección.",
                                            color = Color(0xFF795548), fontSize = 11.sp)
                                    }
                                }
                            }
                        }

                        if (origenSeleccionado.isNotBlank() && origenSeleccionado == destinoSeleccionado) {
                            Text("Origen y destino no pueden ser iguales", color = Color.Red, fontSize = 12.sp)
                        }

                        if (creandoRuta) {
                            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                CircularProgressIndicator(color = smartvanCyan, modifier = Modifier.size(18.dp), strokeWidth = 2.dp)
                                Text("Generando pre-ruta por carretera...", color = Color.Gray, fontSize = 12.sp)
                            }
                        }
                    }
                },
                confirmButton = {
                    Button(
                        onClick = {
                            if (!puedeCrear) return@Button
                            creandoRuta = true
                            scope.launch {
                                try {
                                    val db = network.FirebaseClient.database
                                    // Geocodificar ciudades con Nominatim (OSM gratuito)
                                    val origenCoords = geocodificarCiudad(origenSeleccionado)
                                    val destinoCoords = geocodificarCiudad(destinoSeleccionado)

                                    val preWaypoints = if (origenCoords != null && destinoCoords != null) {
                                        listOf(
                                            mapOf("lat" to origenCoords.first, "lng" to origenCoords.second, "nombre" to origenSeleccionado),
                                            mapOf("lat" to destinoCoords.first, "lng" to destinoCoords.second, "nombre" to destinoSeleccionado)
                                        )
                                    } else {
                                        // Si geocoding falla, crear sin coordenadas
                                        emptyList()
                                    }

                                    db.reference("catalogos/rutas/$nombreRutaGenerado/waypoints").setValue(preWaypoints)
                                    rutas = rutas + nombreRutaGenerado
                                    rutaSeleccionada = nombreRutaGenerado
                                    waypoints = if (origenCoords != null && destinoCoords != null) {
                                        listOf(
                                            WaypointData(origenCoords.first, origenCoords.second, origenSeleccionado),
                                            WaypointData(destinoCoords.first, destinoCoords.second, destinoSeleccionado)
                                        )
                                    } else emptyList()
                                    mensaje = "Ruta '$nombreRutaGenerado' creada. ${if (preWaypoints.isNotEmpty()) "Pre-ruta generada — edita en el mapa para ajustar." else "Agrega puntos en el mapa."}"
                                } catch (e: Exception) {
                                    mensaje = "Error: ${e.message}"
                                }
                                creandoRuta = false
                                mostrarDialogoNuevaRuta = false
                                origenSeleccionado = ""; destinoSeleccionado = ""
                            }
                        },
                        enabled = puedeCrear,
                        colors = ButtonDefaults.buttonColors(containerColor = smartvanCyan)
                    ) {
                        Text(if (creandoRuta) "Generando..." else "Crear Ruta", fontWeight = FontWeight.Bold)
                    }
                },
                dismissButton = {
                    if (!creandoRuta) {
                        TextButton(onClick = {
                            mostrarDialogoNuevaRuta = false
                            origenSeleccionado = ""; destinoSeleccionado = ""
                        }) { Text("Cancelar") }
                    }
                }
            )
        }
    }
}

/** Tarjeta de puntos seleccionables (subidas/bajadas) para agregar al recorrido. */
@Composable
private fun PuntosSeleccionablesCard(
    titulo: String,
    subtitulo: String,
    color: Color,
    puntos: List<ParadaInfo>,
    onAgregar: (ParadaInfo) -> Unit,
    onAgregarTodas: () -> Unit
) {
    Card(
        colors = CardDefaults.cardColors(containerColor = color.copy(alpha = 0.08f)),
        shape = RoundedCornerShape(12.dp),
        border = BorderStroke(1.dp, color.copy(alpha = 0.4f)),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Text(titulo, fontWeight = FontWeight.Bold, color = color, fontSize = 14.sp)
            Text(subtitulo, color = Color.Gray, fontSize = 11.sp)
            Spacer(modifier = Modifier.height(8.dp))
            puntos.forEach { p ->
                Row(
                    modifier = Modifier.fillMaxWidth().padding(vertical = 3.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Icon(Icons.Filled.LocationOn, contentDescription = null, tint = color, modifier = Modifier.size(18.dp))
                    Spacer(modifier = Modifier.width(8.dp))
                    Column(modifier = Modifier.weight(1f)) {
                        Text(p.nombre, fontWeight = FontWeight.Medium, fontSize = 13.sp)
                        val horaTxt = if (p.hora.isNotBlank()) " · ${p.hora}" else ""
                        Text("${p.ciudad} · ${p.lat.toString().take(8)}, ${p.lng.toString().take(9)}$horaTxt", color = Color.Gray, fontSize = 10.sp)
                    }
                    IconButton(onClick = { onAgregar(p) }) {
                        Icon(Icons.Filled.Add, contentDescription = "Agregar", tint = color, modifier = Modifier.size(20.dp))
                    }
                }
            }
            Spacer(modifier = Modifier.height(4.dp))
            Button(
                onClick = onAgregarTodas,
                colors = ButtonDefaults.buttonColors(containerColor = color),
                shape = RoundedCornerShape(8.dp),
                modifier = Modifier.fillMaxWidth()
            ) {
                Text("Agregar Todas (${puntos.size})", fontWeight = FontWeight.Bold, color = Color.White, fontSize = 13.sp)
            }
        }
    }
}

/**
 * Geocodifica una ciudad usando Nominatim (OpenStreetMap) — gratuito, sin API key.
 * Retorna Pair(lat, lng) o null si no encuentra la ciudad.
 */
/**
 * Extrae lat/lng de un link de Google Maps.
 * Soporta formatos:
 *   - https://www.google.com/maps/place/.../@20.5888,-100.3899,17z/...
 *   - https://maps.google.com/?q=20.5888,-100.3899
 *   - https://www.google.com/maps?q=20.5888,-100.3899
 *   - https://maps.app.goo.gl/... (short links — se resuelve el redirect)
 *   - https://goo.gl/maps/...
 *   - Coordenadas directas: "20.5888, -100.3899"
 */
/** Corre TODOS los patrones conocidos de Google Maps sobre un texto (URL o HTML). */
private fun buscarCoordsEnTexto(texto: String): Pair<Double, Double>? {
    // Cota México: un punto de subida/bajada nunca cae fuera del país. El rango global
    // (-90..90) dejaba pasar cualquier número que el regex pescara por accidente.
    fun validar(lat: Double?, lng: Double?): Pair<Double, Double>? =
        if (lat != null && lng != null && lat in 14.0..33.0 && lng in -118.0..-86.0) Pair(lat, lng) else null

    // 1) /@lat,lng  (vista del mapa)
    Regex("""/@(-?\d+\.\d+),(-?\d+\.\d+)""").find(texto)?.let {
        validar(it.groupValues[1].toDoubleOrNull(), it.groupValues[2].toDoubleOrNull())?.let { r -> return r }
    }
    // 2) !3dLAT!4dLNG  (formato de "place" — el más confiable en links de lugar)
    Regex("""!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)""").find(texto)?.let {
        validar(it.groupValues[1].toDoubleOrNull(), it.groupValues[2].toDoubleOrNull())?.let { r -> return r }
    }
    // 3) ?q= / &q= / ?query= / ll= / center=  con lat,lng
    Regex("""[?&](?:q|query|ll|center|destination)=(-?\d+\.\d+),(-?\d+\.\d+)""").find(texto)?.let {
        validar(it.groupValues[1].toDoubleOrNull(), it.groupValues[2].toDoubleOrNull())?.let { r -> return r }
    }
    // 4) /place/lat,lng
    Regex("""/place/(-?\d+\.\d+),(-?\d+\.\d+)""").find(texto)?.let {
        validar(it.groupValues[1].toDoubleOrNull(), it.groupValues[2].toDoubleOrNull())?.let { r -> return r }
    }
    // 5) Coordenadas sueltas "20.5888, -100.3899".
    // Las fronteras (?:^|[^\d.]) y (?:[^\d.]|$) son obligatorias: sin ellas el regex recorta
    // un número más largo — de "3016.9111328125" sacaba "16.9111328125" y lo daba por válido.
    // Se recorren TODAS las coincidencias: si la primera no valida, antes se abandonaba.
    Regex("""(?:^|[^\d.])(-?\d{1,3}\.\d{3,}),\s*(-?\d{1,3}\.\d{3,})(?:[^\d.]|$)""")
        .findAll(texto).forEach {
            validar(it.groupValues[1].toDoubleOrNull(), it.groupValues[2].toDoubleOrNull())?.let { r -> return r }
        }
    return null
}

internal suspend fun extraerCoordsDeGoogleMaps(urlInput: String): Pair<Double, Double>? {
    val url = urlInput.trim()
    if (url.isBlank()) return null

    // Primero: patrones directos sobre el texto pegado.
    buscarCoordsEnTexto(url)?.let { return it }

    // Short link (maps.app.goo.gl / goo.gl/maps): intento local siguiendo el redirect.
    if (url.contains("goo.gl") || url.contains("maps.app") || url.contains("google.")) {
        val local = try {
            val response = network.sharedHttpClient.get(url) {
                header("User-Agent", "Mozilla/5.0 (compatible; SmartVan/1.0)")
            }
            val finalUrl = response.request.url.toString()
            buscarCoordsEnTexto(finalUrl) ?: buscarCoordsEnTexto(response.bodyAsText())
        } catch (_: Exception) { null }
        if (local != null) return local

        // Fallback server-side: en web CORS bloquea el redirect a google.com y el short link
        // termina en una página de consentimiento sin coords. La Cloud Function lo resuelve
        // sin CORS y siguiendo el redirect de forma confiable.
        return try {
            val idToken = network.FirebaseClient.auth.currentUser?.getIdToken(true) ?: ""
            val bodyReq = """{"data":{"appVersion":${network.AppConfig.VERSION_CODE},"url":${kotlinx.serialization.json.JsonPrimitive(url)}}}"""
            val resp = network.sharedHttpClient.post(network.CloudFunctions.RESOLVER_LINK_MAPS) {
                header("Content-Type", "application/json")
                header("Authorization", "Bearer $idToken")
                setBody(bodyReq)
            }
            val txt = resp.bodyAsText()
            if (txt.contains("\"success\":true")) {
                val lat = Regex("\"lat\":(-?\\d+\\.\\d+)").find(txt)?.groupValues?.getOrNull(1)?.toDoubleOrNull()
                val lng = Regex("\"lng\":(-?\\d+\\.\\d+)").find(txt)?.groupValues?.getOrNull(1)?.toDoubleOrNull()
                if (lat != null && lng != null) Pair(lat, lng) else null
            } else null
        } catch (_: Exception) { null }
    }

    return null
}

internal suspend fun geocodificarCiudad(ciudad: String): Pair<Double, Double>? {
    return try {
        val query = "$ciudad, Mexico"
        val url = "https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1"
        val response = network.sharedHttpClient.get(url) {
            header("User-Agent", "SmartVan-KMP/1.0")
        }
        val body = response.bodyAsText()
        // Parse manual — evitar dependencia de serialización extra
        val latMatch = Regex(""""lat"\s*:\s*"([^"]+)"""").find(body)
        val lonMatch = Regex(""""lon"\s*:\s*"([^"]+)"""").find(body)
        if (latMatch != null && lonMatch != null) {
            val lat = latMatch.groupValues[1].toDoubleOrNull()
            val lon = lonMatch.groupValues[1].toDoubleOrNull()
            if (lat != null && lon != null) Pair(lat, lon) else null
        } else null
    } catch (e: Exception) {
        null
    }
}

/** Codifica un string para usar en URLs */
internal fun encodeURIComponent(s: String): String {
    return s.replace(" ", "%20")
        .replace(",", "%2C")
        .replace("á", "%C3%A1").replace("é", "%C3%A9")
        .replace("í", "%C3%AD").replace("ó", "%C3%B3")
        .replace("ú", "%C3%BA").replace("ñ", "%C3%B1")
        .replace("Á", "%C3%81").replace("É", "%C3%89")
        .replace("Í", "%C3%8D").replace("Ó", "%C3%93")
        .replace("Ú", "%C3%9A").replace("Ñ", "%C3%91")
}
